/**
 * Telemetry Health Resolver — pure classification logic for admin observability.
 *
 * Classifies each property/system into a health status based on polling,
 * telemetry recency, and data quality signals. No side effects, no mutations.
 */

// ─── Types ──────────────────────────────────────────────────────────────

export type TelemetryHealthStatus =
  | 'healthy'
  | 'stale'
  | 'no_recent_data'
  | 'never_reported'
  | 'provider_error'
  | 'zero_data_only'
  | 'unknown';

export interface TelemetryHealthInput {
  /** Last successful provider contact (ISO timestamp or null) */
  last_provider_ok_at: string | null;
  /** Last non-empty telemetry received (ISO timestamp or null) */
  last_non_empty_telemetry_at: string | null;
  /** Last poll attempt (ISO timestamp or null) */
  last_poll_at: string | null;
  /** Last poll status string (e.g. "success", "error: ...") */
  last_poll_status: string | null;
  /** Last auth failure timestamp (ISO or null) */
  last_provider_auth_failed_at: string | null;
  /** Consecutive poll failures */
  consecutive_failures: number;
  /** Number of days with daily_energy_summary rows in last 30 days */
  days_with_data_30: number;
  /** Total daily_energy_summary rows ever */
  total_daily_summaries: number;
  /** Whether ALL daily summaries have zero energy (pv + load + grid = 0) */
  all_summaries_zero: boolean;
  /** Evaluation timestamp (defaults to Date.now() if not provided) */
  now?: number;
}

export interface TelemetryHealthResult {
  status: TelemetryHealthStatus;
  recommended_action: string;
}

// ─── Thresholds ─────────────────────────────────────────────────────────

/** Hours before telemetry is considered stale */
export const STALE_THRESHOLD_HOURS = 6;

/** Hours before marking as no_recent_data (vs stale) — effectively "hasn't reported in days" */
export const NO_RECENT_DATA_THRESHOLD_HOURS = 72;

// ─── Resolver ───────────────────────────────────────────────────────────

export function resolveTelemetryHealth(input: TelemetryHealthInput): TelemetryHealthResult {
  const now = input.now ?? Date.now();

  const hasProviderError = isProviderError(input);
  const hasEverReported = input.total_daily_summaries > 0 || input.last_provider_ok_at != null;
  const lastDataMs = getLastDataTimestamp(input);
  const hoursSinceData = lastDataMs != null ? (now - lastDataMs) / (1000 * 60 * 60) : null;

  // 1. Never reported — no telemetry rows, no successful contact ever
  if (!hasEverReported && !input.last_non_empty_telemetry_at) {
    if (hasProviderError) {
      return {
        status: 'provider_error',
        recommended_action: getProviderErrorAction(input),
      };
    }
    return {
      status: 'never_reported',
      recommended_action: 'Verify provider account is connected and device is registered.',
    };
  }

  // 2. Provider error — recent auth failure or persistent poll failures
  if (hasProviderError) {
    return {
      status: 'provider_error',
      recommended_action: getProviderErrorAction(input),
    };
  }

  // 3. Zero data only — has summaries but all energy is zero
  if (input.total_daily_summaries > 0 && input.all_summaries_zero && input.days_with_data_30 === 0) {
    return {
      status: 'zero_data_only',
      recommended_action: 'System reports but all energy values are zero. Verify inverter is producing/consuming.',
    };
  }

  // 4. No recent data — has historical data but nothing in 30 days
  if (hasEverReported && input.days_with_data_30 === 0) {
    return {
      status: 'no_recent_data',
      recommended_action: 'No telemetry in 30 days. Check provider account status and device connectivity.',
    };
  }

  // 5. Stale — has recent-ish data but beyond threshold
  if (hoursSinceData != null && hoursSinceData > NO_RECENT_DATA_THRESHOLD_HOURS) {
    return {
      status: 'no_recent_data',
      recommended_action: 'No telemetry in 72+ hours. Check provider account status and device connectivity.',
    };
  }

  if (hoursSinceData != null && hoursSinceData > STALE_THRESHOLD_HOURS) {
    return {
      status: 'stale',
      recommended_action: `Telemetry is ${Math.round(hoursSinceData)}h stale. Monitor — may recover on next poll cycle.`,
    };
  }

  // 6. Healthy — recent data within threshold
  if (hoursSinceData != null && hoursSinceData <= STALE_THRESHOLD_HOURS) {
    return {
      status: 'healthy',
      recommended_action: 'No action needed.',
    };
  }

  // 7. Unknown — insufficient metadata to determine
  return {
    status: 'unknown',
    recommended_action: 'Unable to determine health. Check system configuration.',
  };
}

// ─── Helpers ────────────────────────────────────────────────────────────

function isProviderError(input: TelemetryHealthInput): boolean {
  // Auth failure more recent than last successful contact
  if (input.last_provider_auth_failed_at) {
    const authFailedAt = new Date(input.last_provider_auth_failed_at).getTime();
    const lastOkAt = input.last_provider_ok_at
      ? new Date(input.last_provider_ok_at).getTime()
      : 0;
    if (authFailedAt > lastOkAt) return true;
  }

  // Poll status contains error
  if (input.last_poll_status && input.last_poll_status.startsWith('error')) {
    // Only if consecutive failures indicate it's ongoing, not a one-off
    if (input.consecutive_failures >= 3) return true;
  }

  // High consecutive failures even without explicit error string
  if (input.consecutive_failures >= 5) return true;

  return false;
}

function getLastDataTimestamp(input: TelemetryHealthInput): number | null {
  // Prefer last_non_empty_telemetry_at, fall back to last_provider_ok_at
  if (input.last_non_empty_telemetry_at) {
    return new Date(input.last_non_empty_telemetry_at).getTime();
  }
  if (input.last_provider_ok_at) {
    return new Date(input.last_provider_ok_at).getTime();
  }
  return null;
}

function getProviderErrorAction(input: TelemetryHealthInput): string {
  const status = input.last_poll_status ?? '';

  if (status.includes('DEVICE_ERROR_USER_DISMATCH') || status.includes('USER_DISMATCH')) {
    return 'EG4 device/user mismatch. Verify the device serial number is registered to the correct EG4 account.';
  }
  if (status.includes('auth') || status.includes('401') || status.includes('invalid token')) {
    return 'Authentication failure. Re-enter provider credentials or re-authorize OAuth.';
  }
  if (status.includes('rate_limit') || status.includes('429')) {
    return 'Rate limited by provider. Will retry automatically — monitor.';
  }
  if (input.consecutive_failures >= 10) {
    return `${input.consecutive_failures} consecutive failures. Provider account may be suspended or device offline.`;
  }
  return `Provider error: ${status || 'unknown'}. Check credentials and device registration.`;
}
